import { PageLoadingIndicator } from '@wrisk/ui-components'
import React, {
  FunctionComponent,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { AnonymousUser, isTokenRequest } from '../../../clients/anonymous'
import { User } from '../../../clients/auth'
import { useAuth } from '../../../hooks/auth'
import airbrake from '../../../infrastructure/airbrake/airbrake'
import { useAnonymous, useApiClientFactory } from '../../../state'
import { useAuthenticate } from '../hooks'
import PrincipalProvider, {
  AnonymousPrincipal,
  Principal,
  UserPrincipal,
} from '../PrincipalProvider'
import { ProposerProvider } from '../proposer'

export const isUser = (user: User | AnonymousUser | undefined): user is User =>
  Boolean(user && (user as User).username)

interface PrincipalManagerProps extends PropsWithChildren {
  user: User | AnonymousUser
}

const PrincipalManager: FunctionComponent<PrincipalManagerProps> = ({
  user,
  children,
}) => {
  const [userState, setUser] = useState<User | AnonymousUser>(user)

  const apiClientFactory = useApiClientFactory()
  const anonymous = useAnonymous()
  const auth = useAuth()

  const principal = useMemo<Principal>(
    () =>
      isUser(userState)
        ? ({
            user: userState,
            isAuthenticated: true,
            isAdmin: false,
            apiClient: apiClientFactory(auth.getTokenSilently),
          } satisfies UserPrincipal)
        : ({
            user: userState,
            isAuthenticated: false,
            isAdmin: false,
            apiClient: apiClientFactory(anonymous.getToken),
            update: async (request) => {
              const update = isTokenRequest(request)
                ? await anonymous.updateByToken(request)
                : await anonymous.updateByForm(request)

              setUser(update)
            },
            refresh: async () => {
              setUser(await anonymous.refresh())
            },
          } satisfies AnonymousPrincipal),
    [userState],
  )

  useEffect(() => {
    userState &&
      airbrake?.addFilter((notice) => {
        notice.context.user = {
          id: userState.userId,
        }
        return notice
      })

    window.dataLayer?.push({
      userId: userState?.userId,
    })
  }, [userState])

  return (
    <PrincipalProvider value={principal}>
      <ProposerProvider>{children}</ProposerProvider>
    </PrincipalProvider>
  )
}

export const UserManager: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { loading, result: user } = useAuthenticate(true)

  return loading ? (
    <PageLoadingIndicator />
  ) : user ? (
    <PrincipalManager user={user}>{children}</PrincipalManager>
  ) : null
}
